import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const WalletConnectSection = ({ 
  walletConnected, 
  walletBalance, 
  selectedWallet, 
  onConnect, 
  loading 
}) => {
  const { isDarkMode } = useTheme();
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  
  // Debug logging for wallet balance and update balance loading state
  useEffect(() => {
    if (walletBalance) {
      console.log('WalletConnectSection received walletBalance:', walletBalance);
      console.log('SOL balance type:', typeof walletBalance.solBalance);
      console.log('SOL balance value:', walletBalance.solBalance);
      setBalanceLoading(false);
    } else {
      // If wallet is connected but balance isn't loaded yet, set loading state
      if (walletConnected && selectedWallet && !selectedWallet.isGroup) {
        setBalanceLoading(true);
      }
    }
  }, [walletBalance, walletConnected, selectedWallet]);
  
  
  const handleConnect = async () => {
    if (connecting) return;
    
    setConnecting(true);
    setConnectError(null);
    setBalanceLoading(true);
    
    try {
      const result = await onConnect();
      if (!result) {
        // Don't show generic error - the parent component will set a more specific error
        // setConnectError('Failed to connect wallet. Please try again.');
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setConnectError(error.message || 'Failed to connect wallet');
    } finally {
      setConnecting(false);
      // Balance loading state will be cleared when walletBalance updates
    }
  };
  
  // Function to copy address to clipboard
  const copyToClipboard = (address) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy address: ', err);
      });
  };
  
  // Format wallet address to show first 4 and last 4 characters
  const formatWalletAddress = (address) => {
    if (!address || address.length < 10) return address;
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };
  
  // Format token amount with thousands separators
  const formatTokenAmount = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    
    try {
      // Clean the input if it's a string
      let cleanedAmount = amount;
      if (typeof amount === 'string') {
        // Remove any non-numeric characters except decimal point
        cleanedAmount = amount.replace(/[^0-9.]/g, '');
      }
      
      // Convert to number
      let num = parseFloat(cleanedAmount);
      
      // Check if it's a valid number
      if (isNaN(num)) {
        console.warn('Invalid number for formatTokenAmount:', amount);
        return '0.00';
      }
      
      // For debugging
      console.log(`Formatting amount: ${amount} (${typeof amount}) -> ${num} (${typeof num})`);
      
      // Format with correct decimal places and thousands separator
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6 // Allow more decimal places for small values
      });
    } catch (error) {
      console.error('Error formatting token amount:', error, 'for amount:', amount);
      return '0.00';
    }
  };
  
  // Special case for SOL balance display - explicitly ensure it's a number
  const getSolBalance = () => {
    if (!walletBalance || walletBalance.solBalance === undefined || walletBalance.solBalance === null) {
      console.log('No SOL balance found in wallet data');
      return '0.00';
    }
    
    let solBalance;
    try {
      // Handle various input formats
      if (typeof walletBalance.solBalance === 'string') {
        // Remove any non-numeric characters except decimal point
        const cleanedStr = walletBalance.solBalance.replace(/[^0-9.]/g, '');
        solBalance = parseFloat(cleanedStr);
      } else {
        solBalance = Number(walletBalance.solBalance);
      }
      
      console.log(`Getting SOL balance: raw=${walletBalance.solBalance}, cleaned=${solBalance}`);
      
      if (isNaN(solBalance)) {
        console.warn(`Invalid SOL balance format: ${walletBalance.solBalance}`);
        return '0.00';
      }
      
      return solBalance.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      });
    } catch (error) {
      console.error('Error formatting SOL balance:', error);
      return '0.00';
    }
  };
  
  return (
    <div className="w-full sm:w-auto">
      {!walletConnected ? (
        <div className="flex flex-col items-end">
          <div className="flex items-center w-full sm:w-auto">
            {selectedWallet ? (
              <button
                onClick={handleConnect}
                disabled={connecting || loading}
                className="w-full sm:w-auto px-3 sm:px-5 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm font-medium flex items-center justify-center disabled:bg-purple-400 text-xs sm:text-sm"
              >
                {connecting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="hidden sm:inline">Connecting...</span>
                    <span className="sm:hidden">Connecting</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Connect Wallet</span>
                    <span className="sm:hidden">Connect</span>
                    <span className="ml-1 sm:ml-2 text-xs bg-purple-800 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      {formatWalletAddress(selectedWallet.address)}
                    </span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleConnect}
                disabled={connecting || loading}
                className="w-full sm:w-auto px-3 sm:px-5 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm font-medium disabled:bg-purple-400 text-xs sm:text-sm"
              >
                {connecting ? (
                  <>
                    <span className="hidden sm:inline">Connecting...</span>
                    <span className="sm:hidden">Connecting</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Connect Wallet</span>
                    <span className="sm:hidden">Connect</span>
                  </>
                )}
              </button>
            )}
          </div>
          {connectError && (
            <div className="text-xs text-red-600 dark:text-red-400 mt-1 transition-colors">{connectError}</div>
          )}
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 lg:space-x-6">
          {balanceLoading ? (
            <div className="flex items-center space-x-2">
              <svg className="animate-spin h-4 w-4 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Loading balance...</span>
            </div>
          ) : (
            <>
              {walletBalance && (
                <>
                  {selectedWallet && selectedWallet.isGroup ? (
                    <div className="flex items-center text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <span className="font-medium text-purple-700 dark:text-purple-300 text-xs sm:text-sm">
                        Group selected
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <span className="font-bold text-purple-700 dark:text-purple-300 mr-1 text-xs sm:text-sm">
                          {getSolBalance()}
                        </span>
                        <span className="text-xs sm:text-sm">SOL</span>
                      </div>
                      {walletBalance.tokens && walletBalance.tokens.length > 0 ? (
                        walletBalance.tokens.slice(0, 2).map((token, index) => (
                          <div key={token.symbol || index} className="flex items-center text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                            <span className="font-bold text-purple-700 dark:text-purple-300 mr-1 text-xs sm:text-sm">
                              {formatTokenAmount(token.balance)}
                            </span>
                            <span className="text-xs sm:text-sm">{token.symbol}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm italic hidden sm:block">No other tokens found</div>
                      )}
                      {walletBalance.tokens && walletBalance.tokens.length > 2 && (
                        <div className="text-gray-500 dark:text-gray-400 text-xs italic">+{walletBalance.tokens.length - 2} more</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
          <div className="flex items-center">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-1 sm:mr-2"></div>
            <span className="text-xs sm:text-sm text-green-700 dark:text-green-400">Connected</span>
            {selectedWallet && (
              <div className="flex items-center ml-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatWalletAddress(selectedWallet.address)}
                </span>
                <button
                  onClick={() => copyToClipboard(selectedWallet.address)}
                  className="ml-1 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full transition-colors focus:outline-none"
                  title="Copy wallet address"
                >
                  {copiedAddress ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletConnectSection;